import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import client from "./client";
import API_ENDPOINTS from "../constants/api-endpoints";
import {
  TCompanyUser,
  TCompanyBranch,
  TCompanyDivision,
  TCompanyHoliday,
  TCompanyFeatureOverride,
  TUserSkill,
  TTeam,
  TTeamMember,
} from "../types";

type Callbacks = { onSuccess?: (res: any) => void; onError?: (err: any) => void };

export const useCompanyUsers = (companyId: number) => {
  return useQuery({
    queryKey: [API_ENDPOINTS.COMPANY_USERS(companyId)],
    queryFn: () => client.get(API_ENDPOINTS.COMPANY_USERS(companyId)),
    select: (res: any) => res.data.data as TCompanyUser[],
    refetchOnWindowFocus: false,
    enabled: !!companyId,
  });
};

export const useCompanyUserCreate = (companyId: number, { onSuccess, onError }: Callbacks = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => client.post(API_ENDPOINTS.COMPANY_USER_CREATE(companyId), data),
    onSuccess: (res) => { queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.COMPANY_USERS(companyId)] }); onSuccess?.(res); },
    onError: (err) => onError?.(err),
  });
};
export const useCompanyUserUpdate = (companyId: number, { onSuccess, onError }: Callbacks = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => client.post(API_ENDPOINTS.COMPANY_USER_UPDATE(companyId), data),
    onSuccess: (res) => { queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.COMPANY_USERS(companyId)] }); onSuccess?.(res); },
    onError: (err) => onError?.(err),
  });
};
export const useCompanyUserDelete = (companyId: number, { onSuccess, onError }: Callbacks = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => client.post(API_ENDPOINTS.COMPANY_USER_DELETE(companyId), { id }),
    onSuccess: (res) => { queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.COMPANY_USERS(companyId)] }); onSuccess?.(res); },
    onError: (err) => onError?.(err),
  });
};

export const useCompanyBranches = (companyId: number) => {
  return useQuery({
    queryKey: [API_ENDPOINTS.COMPANY_CONFIG_BRANCHES(companyId)],
    queryFn: () => client.get(API_ENDPOINTS.COMPANY_CONFIG_BRANCHES(companyId)),
    select: (res: any) => res.data.data as TCompanyBranch[],
    refetchOnWindowFocus: false,
    enabled: !!companyId,
  });
};

export const useCompanyDivisions = (companyId: number) => {
  return useQuery({
    queryKey: [API_ENDPOINTS.COMPANY_CONFIG_DIVISIONS(companyId)],
    queryFn: () => client.get(API_ENDPOINTS.COMPANY_CONFIG_DIVISIONS(companyId)),
    select: (res: any) => res.data.data as TCompanyDivision[],
    refetchOnWindowFocus: false,
    enabled: !!companyId,
  });
};

export const useCompanyHolidays = (companyId: number) => {
  return useQuery({
    queryKey: [API_ENDPOINTS.COMPANY_CONFIG_HOLIDAYS(companyId)],
    queryFn: () => client.get(API_ENDPOINTS.COMPANY_CONFIG_HOLIDAYS(companyId)),
    select: (res: any) => res.data.data as TCompanyHoliday[],
    refetchOnWindowFocus: false,
    enabled: !!companyId,
  });
};

export const useCompanyBranchCreate = (companyId: number, { onSuccess, onError }: Callbacks = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => client.post(API_ENDPOINTS.COMPANY_CONFIG_BRANCH_CREATE(companyId), data),
    onSuccess: (res) => { queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.COMPANY_CONFIG_BRANCHES(companyId)] }); onSuccess?.(res); },
    onError: (err) => onError?.(err),
  });
};
export const useCompanyBranchUpdate = (companyId: number, { onSuccess, onError }: Callbacks = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => client.post(API_ENDPOINTS.COMPANY_CONFIG_BRANCH_UPDATE(companyId), data),
    onSuccess: (res) => { queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.COMPANY_CONFIG_BRANCHES(companyId)] }); onSuccess?.(res); },
    onError: (err) => onError?.(err),
  });
};
export const useCompanyBranchDelete = (companyId: number, { onSuccess, onError }: Callbacks = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => client.post(API_ENDPOINTS.COMPANY_CONFIG_BRANCH_DELETE(companyId), { id }),
    onSuccess: (res) => { queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.COMPANY_CONFIG_BRANCHES(companyId)] }); onSuccess?.(res); },
    onError: (err) => onError?.(err),
  });
};

export const useCompanyDivisionCreate = (companyId: number, { onSuccess, onError }: Callbacks = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => client.post(API_ENDPOINTS.COMPANY_CONFIG_DIVISION_CREATE(companyId), data),
    onSuccess: (res) => { queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.COMPANY_CONFIG_DIVISIONS(companyId)] }); onSuccess?.(res); },
    onError: (err) => onError?.(err),
  });
};
export const useCompanyDivisionUpdate = (companyId: number, { onSuccess, onError }: Callbacks = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => client.post(API_ENDPOINTS.COMPANY_CONFIG_DIVISION_UPDATE(companyId), data),
    onSuccess: (res) => { queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.COMPANY_CONFIG_DIVISIONS(companyId)] }); onSuccess?.(res); },
    onError: (err) => onError?.(err),
  });
};
export const useCompanyDivisionDelete = (companyId: number, { onSuccess, onError }: Callbacks = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => client.post(API_ENDPOINTS.COMPANY_CONFIG_DIVISION_DELETE(companyId), { id }),
    onSuccess: (res) => { queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.COMPANY_CONFIG_DIVISIONS(companyId)] }); onSuccess?.(res); },
    onError: (err) => onError?.(err),
  });
};

export const useCompanyHolidayCreate = (companyId: number, { onSuccess, onError }: Callbacks = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => client.post(API_ENDPOINTS.COMPANY_CONFIG_HOLIDAY_CREATE(companyId), data),
    onSuccess: (res) => { queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.COMPANY_CONFIG_HOLIDAYS(companyId)] }); onSuccess?.(res); },
    onError: (err) => onError?.(err),
  });
};
export const useCompanyHolidayUpdate = (companyId: number, { onSuccess, onError }: Callbacks = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => client.post(API_ENDPOINTS.COMPANY_CONFIG_HOLIDAY_UPDATE(companyId), data),
    onSuccess: (res) => { queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.COMPANY_CONFIG_HOLIDAYS(companyId)] }); onSuccess?.(res); },
    onError: (err) => onError?.(err),
  });
};
export const useCompanyHolidayDelete = (companyId: number, { onSuccess, onError }: Callbacks = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => client.post(API_ENDPOINTS.COMPANY_CONFIG_HOLIDAY_DELETE(companyId), { id }),
    onSuccess: (res) => { queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.COMPANY_CONFIG_HOLIDAYS(companyId)] }); onSuccess?.(res); },
    onError: (err) => onError?.(err),
  });
};

export const useCompanyTeamCreate = (companyId: number, { onSuccess, onError }: Callbacks = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => client.post(API_ENDPOINTS.COMPANY_CONFIG_TEAM_CREATE(companyId), data),
    onSuccess: (res) => { queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.COMPANY_CONFIG_TEAMS(companyId)] }); onSuccess?.(res); },
    onError: (err) => onError?.(err),
  });
};
export const useCompanyTeamUpdate = (companyId: number, { onSuccess, onError }: Callbacks = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => client.post(API_ENDPOINTS.COMPANY_CONFIG_TEAM_UPDATE(companyId), data),
    onSuccess: (res) => { queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.COMPANY_CONFIG_TEAMS(companyId)] }); onSuccess?.(res); },
    onError: (err) => onError?.(err),
  });
};
export const useCompanyTeamDelete = (companyId: number, { onSuccess, onError }: Callbacks = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => client.post(API_ENDPOINTS.COMPANY_CONFIG_TEAM_DELETE(companyId), { id }),
    onSuccess: (res) => { queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.COMPANY_CONFIG_TEAMS(companyId)] }); onSuccess?.(res); },
    onError: (err) => onError?.(err),
  });
};

export const useCompanyFeatureOverrides = (companyId: number) => {
  return useQuery({
    queryKey: [API_ENDPOINTS.COMPANY_CONFIG_FEATURE_OVERRIDES(companyId)],
    queryFn: () => client.get(API_ENDPOINTS.COMPANY_CONFIG_FEATURE_OVERRIDES(companyId)),
    select: (res: any) => res.data.data as TCompanyFeatureOverride[],
    refetchOnWindowFocus: false,
    enabled: !!companyId,
  });
};

export const useCompanyFeatureOverrideSet = (companyId: number, { onSuccess, onError }: Callbacks = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { feature_key: string; is_enabled: boolean; value?: string; override_reason?: string; expires_at?: string }) =>
      client.post(API_ENDPOINTS.COMPANY_CONFIG_FEATURE_OVERRIDES(companyId), data),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.COMPANY_CONFIG_FEATURE_OVERRIDES(companyId)] });
      onSuccess?.(res);
    },
    onError: (err) => onError?.(err),
  });
};

export const useCompanyFeatureOverrideDelete = (companyId: number, { onSuccess, onError }: Callbacks = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (featureKey: string) =>
      client.delete(`${API_ENDPOINTS.COMPANY_CONFIG_FEATURE_OVERRIDES(companyId)}/${featureKey}`),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.COMPANY_CONFIG_FEATURE_OVERRIDES(companyId)] });
      onSuccess?.(res);
    },
    onError: (err) => onError?.(err),
  });
};

export const useCompanyUserSkills = (companyId: number) => {
  return useQuery({
    queryKey: [API_ENDPOINTS.COMPANY_CONFIG_USER_SKILLS(companyId)],
    queryFn: () => client.get(API_ENDPOINTS.COMPANY_CONFIG_USER_SKILLS(companyId)),
    select: (res: any) => res.data.data as TUserSkill[],
    refetchOnWindowFocus: false,
    enabled: !!companyId,
  });
};

export const useCompanyTeams = (companyId: number) => {
  return useQuery({
    queryKey: [API_ENDPOINTS.COMPANY_CONFIG_TEAMS(companyId)],
    queryFn: () => client.get(API_ENDPOINTS.COMPANY_CONFIG_TEAMS(companyId)),
    select: (res: any) => res.data.data as TTeam[],
    refetchOnWindowFocus: false,
    enabled: !!companyId,
  });
};

export const useCompanyTeamMembers = (companyId: number, teamId: number) => {
  return useQuery({
    queryKey: [API_ENDPOINTS.COMPANY_CONFIG_TEAM_MEMBERS(companyId, teamId)],
    queryFn: () => client.get(API_ENDPOINTS.COMPANY_CONFIG_TEAM_MEMBERS(companyId, teamId)),
    select: (res: any) => res.data.data as TTeamMember[],
    refetchOnWindowFocus: false,
    enabled: !!companyId && !!teamId,
  });
};
